import React, { useEffect, useState } from "react";
import axios from "axios";
import api from "../axiosConfig";

const CATEGORY_OPTIONS = [
  { value: "bug", label: "Report a bug" },
  { value: "incorrect_info", label: "Incorrect information" },
  { value: "request", label: "Request a school or professor" },
  { value: "general", label: "General feedback" },
  { value: "other", label: "Other" },
];

const PLACEHOLDERS = {
  bug: "What happened? What did you expect instead?",
  incorrect_info:
    "What's incorrect? Please include details if you can.",
  request: "Which school or professor should we add?",
  general: "What's on your mind?",
  other: "Please share your feedback.",
};

const anonymousFeedbackApi = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api/",
});

/**
 * Feedback form modal; POSTs to /api/feedback/ then shows success and auto-closes.
 */
const FeedbackModal = ({ open, onClose, requireEmailCaptcha = false }) => {
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [captchaA, setCaptchaA] = useState(0);
  const [captchaB, setCaptchaB] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const regenerateCaptcha = () => {
    const a = Math.floor(Math.random() * 8) + 2;
    const b = Math.floor(Math.random() * 8) + 2;
    setCaptchaA(a);
    setCaptchaB(b);
    setCaptchaAnswer("");
  };

  useEffect(() => {
    if (open && requireEmailCaptcha) {
      setCategory("bug");
    }
  }, [open, requireEmailCaptcha]);

  useEffect(() => {
    if (!open) {
      setSent(false);
      setCategory("general");
      setMessage("");
      setEmail("");
      setSubmitError("");
      setSubmitting(false);
      regenerateCaptcha();
    }
  }, [open]);

  useEffect(() => {
    if (open && requireEmailCaptcha && (captchaA === 0 || captchaB === 0)) {
      regenerateCaptcha();
    }
  }, [open, requireEmailCaptcha, captchaA, captchaB]);

  useEffect(() => {
    if (!sent || !open) return;
    const t = setTimeout(() => {
      onClose();
    }, 2000);
    return () => clearTimeout(t);
  }, [sent, open, onClose]);

  if (!open) return null;

  const placeholder = PLACEHOLDERS[category] ?? PLACEHOLDERS.other;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    const trimmedEmail = email.trim();
    if (!trimmed) {
      setSubmitError("Please enter a message.");
      return;
    }
    if (requireEmailCaptcha) {
      if (!trimmedEmail) {
        setSubmitError("Please enter your email.");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setSubmitError("Please enter a valid email address.");
        return;
      }
      if (Number(captchaAnswer) !== captchaA + captchaB) {
        setSubmitError("CAPTCHA answer is incorrect.");
        regenerateCaptcha();
        return;
      }
    }

    setSubmitError("");
    setSubmitting(true);

    const categoryLabel =
      CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category;

    try {
      const client = requireEmailCaptcha ? anonymousFeedbackApi : api;
      await client.post("/feedback/", {
        category,
        category_label: categoryLabel,
        message: trimmed,
        contact_email: trimmedEmail,
      });
      setSent(true);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Could not send feedback. Please try again.";
      setSubmitError(typeof msg === "string" ? msg : "Could not send feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !sent && !submitting) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-3 sm:p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={`relative flex w-full max-w-md flex-col rounded-lg bg-white p-4 shadow-lg sm:p-6 ${
          sent ? "min-h-[220px] sm:min-h-[260px]" : "max-h-[90vh] overflow-y-auto"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={sent ? undefined : "feedback-modal-title"}
        onClick={(e) => e.stopPropagation()}
      >
        {!sent ? (
          <>
            <button
              onClick={onClose}
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 sm:right-4 sm:top-4"
              type="button"
              aria-label="Close feedback form"
              disabled={submitting}
            >
              <span className="material-icons text-2xl">close</span>
            </button>

            <h2
              id="feedback-modal-title"
              className="mb-5 pr-10 text-center text-xl font-semibold sm:mb-6 sm:text-2xl"
            >
              Give feedback
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {submitError && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  {submitError}
                </p>
              )}

              <div>
                <label
                  htmlFor="feedback-category"
                  className="mb-1.5 block text-left text-sm font-medium text-gray-800"
                >
                  What would you like to share?
                </label>
                <select
                  id="feedback-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-900 focus:border-[#0196BF] focus:outline-none focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 disabled:opacity-60"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {requireEmailCaptcha && (
                <div>
                  <label
                    htmlFor="feedback-email"
                    className="mb-1.5 block text-left text-sm font-medium text-gray-800"
                  >
                    Your email
                  </label>
                  <input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={submitting}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-900 placeholder:text-gray-400 focus:border-[#0196BF] focus:outline-none focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 disabled:opacity-60"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="feedback-message"
                  className="mb-1.5 block text-left text-sm font-medium text-gray-800"
                >
                  Tell us more
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  required
                  disabled={submitting}
                  placeholder={placeholder}
                  className="w-full resize-y rounded-lg border border-gray-300 p-3 text-gray-900 placeholder:text-gray-400 focus:border-[#0196BF] focus:outline-none focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 disabled:opacity-60"
                />
              </div>

              {requireEmailCaptcha && (
                <div>
                  <label
                    htmlFor="feedback-captcha"
                    className="mb-1.5 block text-left text-sm font-medium text-gray-800"
                  >
                    CAPTCHA: What is {captchaA} + {captchaB}?
                  </label>
                  <input
                    id="feedback-captcha"
                    type="text"
                    inputMode="numeric"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    required
                    disabled={submitting}
                    className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-900 placeholder:text-gray-400 focus:border-[#0196BF] focus:outline-none focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 disabled:opacity-60"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-[#0196BF] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#017BA3] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Submit feedback"}
              </button>
            </form>
          </>
        ) : (
          <div
            className="flex flex-1 flex-col items-center justify-center px-8 py-12 text-center"
            role="status"
          >
            <span
              className="text-5xl font-light leading-none text-[#0196BF] sm:text-6xl"
              aria-hidden
            >
              ✓
            </span>
            <p className="mt-4 text-base font-medium text-gray-800 sm:text-lg">
              Feedback sent
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
